import { useEffect, useMemo, useState } from "react";
import { NavLink, useFetcher } from "react-router-dom";
import { toast } from "sonner";
import { FlaskConical, GitCompare, Pencil, Rocket, Trash2, Undo2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VersionLineage } from "@/components/dialer/versions/version-lineage";
import { DeployDialog } from "@/components/dialer/versions/deploy-dialog";
import { RollbackDialog } from "@/components/dialer/versions/rollback-dialog";
import { ActionWrapper } from "@/utils/wretch";
import { deployTargets, WorkflowLineage, WorkflowVersionSummary } from "@/types/dialer/workflow";
import { formatParis } from "@/utils/dialer-datetime";
import { workflowEditorPath } from "@/utils/dialer-breadcrumbs";
import { FieldGrid, StateBlock } from "@/components/layout-2/ds";

type VersionsTabProps = {
    lineage: WorkflowLineage;
    /** Reload the tab and the record around it, once a version has moved. */
    onMutate?: () => void;
};

/**
 * The right-hand side of a comparison, dropped when it becomes the version being read.
 *
 * Comparing a version with itself is not a comparison, and choosing in the frieze the
 * version already picked here is the easy way into that state: the button stayed enabled on
 * a link to « v2 → v2 » while the picker showed nothing, since its list excludes the version
 * being read. Derived rather than reset by an effect, so the picker and the button cannot
 * disagree for a frame.
 */
export function comparisonTarget(picked: string, selectedVersionNumber: number | null | undefined): string {
    return picked && picked !== String(selectedVersionNumber) ? picked : "";
}

/**
 * The version frieze of one workflow, as the `workflow-versions` tab of its generic
 * detail page.
 *
 * This is the part of a workflow that could not be declared as fields. A branch's
 * identity is five values and belongs in sections; its content is a lineage of immutable
 * snapshots, each carrying a verdict, a deployment state and half a dozen gestures that
 * only make sense on the version you are pointing at. A generic tab could render the
 * numbers and would lose all of it.
 *
 * Every gesture that changes something reads `lineage.permissions`, which the server
 * computes with the same `can_write` its own endpoints are gated on, so a reader profile
 * is shown the frieze and no buttons rather than buttons that answer `403`.
 */
export function VersionsTab({ lineage, onMutate }: VersionsTabProps) {
    const workflow = lineage.workflow;
    const permissions = lineage.permissions;
    const discardFetcher = useFetcher<ActionWrapper<unknown>>();
    const [selectedId, setSelectedId] = useState<number | null>(lineage.versions[0]?.id ?? null);
    const [compareWith, setCompareWith] = useState<string>("");
    const [deploying, setDeploying] = useState<WorkflowVersionSummary | null>(null);
    const [rollingBack, setRollingBack] = useState<WorkflowVersionSummary | null>(null);

    const selected = useMemo(() => lineage.versions.find(version => version.id === selectedId) ?? lineage.versions[0], [lineage, selectedId]);

    const targets = useMemo(() => deployTargets(lineage), [lineage]);

    const compareTarget = comparisonTarget(compareWith, selected?.version);

    useEffect(() => {
        if (discardFetcher.state !== "idle" || !discardFetcher.data) return;
        if (discardFetcher.data.success) {
            toast.success("Undeployed version deleted");
            onMutate?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [discardFetcher.state, discardFetcher.data]);

    // Whether a draft *can* be thrown away is the server's call — it knows about cards,
    // scenarios and deployments — so the button obeys `deletable`, and `edit` answers
    // whether this reader may throw anything away at all.
    const canDiscard = (version: WorkflowVersionSummary) => version.deletable && Boolean(permissions?.edit);

    if (lineage.versions.length === 0) {
        return <StateBlock message="This branch has no version yet. Open it on the canvas and create version 1 from the draft." />;
    }

    /**
     * What the selected version is, unfolded by the frieze under the row it describes.
     *
     * It used to be a card of its own after the frieze, and read as one more version: same
     * white surface and same border as a row, opening on the very `VersionBadge` and
     * `ScenarioPill` the rows already carry, under no title, and drifting further from its
     * row with every version published. Handing it to `VersionLineage` makes the belonging
     * positional instead of inferred, and the badge, the pill and the note are dropped here
     * because the row two pixels above already carries all three — repeating them, verbatim
     * and at the same weight, was most of what suggested « another version ».
     *
     * Unfolding rather than splitting the tab in two columns is a measurement, not a taste:
     * this tab is itself the left column of the generic detail page, 460px wide on a
     * 1059px viewport, and half of that holds neither the definition grid nor the seven
     * gestures. Unfolding costs vertical room instead, which the frieze can afford because
     * it is ordered newest first: what an open panel pushes down is older versions, never
     * the rows above the one being read.
     *
     * It stays a function rather than a component of its own because every gesture on it
     * reads the tab's state: the discard fetcher, the two dialogs, the comparison target.
     */
    const versionDetail = (version: WorkflowVersionSummary) => (
        <div className="space-y-4">
            <FieldGrid
                items={[
                    { label: "Author", value: version.created_by?.name },
                    { label: "Created", value: formatParis(version.created_at) },
                    { label: "Active cards pinned", value: <span className="tabular-nums">{version.cards_active}</span> },
                    {
                        label: "Deployed on",
                        value:
                            version.deployed_in.length === 0 ? null : (
                                <span className="flex flex-wrap gap-1">
                                    {version.deployed_in.map(target => (
                                        <Badge key={`${target.campaign_code}-${target.slot}`} tone="info">
                                            {target.slot} @ {target.campaign_code}
                                        </Badge>
                                    ))}
                                </span>
                            )
                    }
                ]}
            />

            {version.issue_counts?.error > 0 && (
                <Alert variant="destructive">
                    <AlertTitle>
                        {version.issue_counts.error} problem{version.issue_counts.error > 1 ? "s" : ""} prevent this version from being deployed
                    </AlertTitle>
                    <AlertDescription>Open the graph to see them on the canvas.</AlertDescription>
                </Alert>
            )}

            <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" asChild>
                    <NavLink to={`/outils/campagnes/workflows/${workflow.code}/v/${version.version}`}>View graph</NavLink>
                </Button>
                {permissions?.edit && (
                    <Button size="sm" variant="outline" asChild>
                        <NavLink to={workflowEditorPath(workflow.id, { from_version: version.version })}>
                            <Pencil className="mr-1 h-4 w-4" /> Edit on canvas
                        </NavLink>
                    </Button>
                )}
                <Button size="sm" variant="outline" asChild>
                    <NavLink to={`/outils/campagnes/workflows/${workflow.code}/v/${version.version}/simulateur`}>
                        <FlaskConical className="mr-1 h-4 w-4" /> Simulate
                    </NavLink>
                </Button>
                <Button size="sm" variant="outline" asChild>
                    <NavLink to={`/outils/campagnes/workflows/${workflow.code}/v/${version.version}/scenarios`}>Scenarios</NavLink>
                </Button>
                {permissions?.deploy && (
                    <Button size="sm" variant="outline" onClick={() => setDeploying(version)} disabled={targets.length === 0}>
                        <Rocket className="mr-1 h-4 w-4" /> Deploy
                    </Button>
                )}
                {permissions?.deploy && version.status !== "draft" && version.deployed_in.length === 0 && (
                    <Button size="sm" variant="outline" onClick={() => setRollingBack(version)} disabled={targets.length === 0}>
                        <Undo2 className="mr-1 h-4 w-4" /> Roll back to this version
                    </Button>
                )}
                {canDiscard(version) && (
                    <Button
                        size="sm"
                        variant="destructive_outline"
                        disabled={discardFetcher.state !== "idle"}
                        onClick={() =>
                            discardFetcher.submit(
                                {},
                                { method: "post", action: `/outils/campagnes/workflows/${workflow.code}?action=discard-draft&version_id=${version.id}`, encType: "application/json" }
                            )
                        }
                    >
                        <Trash2 className="mr-1 h-4 w-4" /> Delete undeployed version
                    </Button>
                )}
            </div>

            <div className="flex flex-wrap items-end gap-2 border-t pt-3">
                <div className="min-w-[200px] flex-1">
                    <p className="mb-1 text-xs text-gray-600">Compare with…</p>
                    <Select value={compareTarget} onValueChange={setCompareWith}>
                        <SelectTrigger>
                            <SelectValue placeholder="Pick another version" />
                        </SelectTrigger>
                        <SelectContent>
                            {lineage.versions
                                .filter(candidate => candidate.version !== version.version)
                                .map(candidate => (
                                    <SelectItem key={candidate.id} value={String(candidate.version)}>
                                        v{candidate.version} — {candidate.status_badge?.label ?? candidate.status}
                                    </SelectItem>
                                ))}
                        </SelectContent>
                    </Select>
                </div>
                <Button size="sm" variant="outline" disabled={!compareTarget} asChild={Boolean(compareTarget)}>
                    {compareTarget ? (
                        <NavLink to={`/outils/campagnes/workflows/${workflow.code}/v/${version.version}/comparer/${compareTarget}`}>
                            <GitCompare className="mr-1 h-4 w-4" /> Compare
                        </NavLink>
                    ) : (
                        <span>
                            <GitCompare className="mr-1 inline h-4 w-4" /> Compare
                        </span>
                    )}
                </Button>
            </div>
        </div>
    );

    return (
        <div className="mt-2 space-y-4">
            <VersionLineage versions={lineage.versions} selectedVersionId={selected?.id ?? null} onSelect={picked => setSelectedId(picked.id)} renderDetail={versionDetail} />

            {deploying && (
                <DeployDialog
                    open
                    onOpenChange={open => !open && setDeploying(null)}
                    workflowCode={workflow.code}
                    workflowName={workflow.name}
                    versionId={deploying.id}
                    versionNumber={deploying.version}
                    targets={targets}
                    onDone={() => onMutate?.()}
                />
            )}
            {rollingBack && (
                <RollbackDialog
                    open
                    onOpenChange={open => !open && setRollingBack(null)}
                    workflowCode={workflow.code}
                    workflowName={workflow.name}
                    versionId={rollingBack.id}
                    versionNumber={rollingBack.version}
                    targets={targets}
                    onDone={() => onMutate?.()}
                />
            )}
        </div>
    );
}
